import React, { useCallback, useEffect, useState } from "react";
import { Button, Text, UploadFile, UploadedFile } from "@zorni/components-lib";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { UPLOAD_RESOURCES, UPLOAD_TYPES } from "../../../../common/model/enum";
import { useUpload } from "../../../../../common/hooks/useUpload";
import { useOperation } from "../../../../hooks/useOperation";
import {
  STANDARD_UPLOAD_EXTENSIONS,
  getAcceptAttribute,
} from "../../../../../common/upload/uploadFileRules";
import { useUploadFileGuard } from "../../../../../common/upload/useUploadFileGuard";
import UploadFileError from "../../../../../common/upload/UploadFileError";
import styles from "./UploadImageField.module.css";

const UploadImageField = ({ label, value, onChange, autoUpload = false }) => {
  const { t } = useTranslation();
  const { uploadFile, updateFile, deleteFile, getUploadIdFromUrl } = useUpload();
  const { showToast } = useOperation();
  const [fileList, setFileList] = useState([]);
  const { guardOnUpload, fileError } = useUploadFileGuard(STANDARD_UPLOAD_EXTENSIONS);
  const [isLocalUploading, setIsLocalUploading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const handleFilePick = (files) => {
    if (!Array.isArray(files) || files.length === 0) {
      setFileList([]);
      return;
    }
    // Business rule: one photo max per entity.
    setFileList([files[0]]);
  };

  const handleUpload = useCallback(
    async (pickedFile) => {
      const file = pickedFile || fileList[0];
      if (!file) return;

      try {
        setIsLocalUploading(true);
        const uploadId = getUploadIdFromUrl(value);
        const result = uploadId
          ? await updateFile(uploadId, file, UPLOAD_RESOURCES.TRIP, UPLOAD_TYPES.PHOTO)
          : await uploadFile(file, UPLOAD_RESOURCES.TRIP, UPLOAD_TYPES.PHOTO);

        // API shape can be either { fileLink, ... } or { data: { fileLink, ... }, ... }
        const nextUrl = result?.fileLink || result?.data?.fileLink || "";
        if (nextUrl) {
          onChange(nextUrl);
        }
        setFileList([]);
      } catch (err) {
        console.error("Error uploading file:", err);
        showToast(
          t("tripPublication.addTrip.upload.error", "Le fichier n'a pas pu être envoyé."),
          "error"
        );
        setFileList([]);
      } finally {
        setIsLocalUploading(false);
      }
    },
    [fileList, getUploadIdFromUrl, onChange, showToast, t, updateFile, uploadFile, value]
  );

  useEffect(() => {
    if (!autoUpload || !fileList?.length) return;
    handleUpload(fileList[0]);
  }, [autoUpload, fileList, handleUpload]);

  const handleRemoveUploaded = async () => {
    if (!value || isDeleting || isLocalUploading) return;
    const uploadId = getUploadIdFromUrl(value);
    try {
      setIsDeleting(true);
      if (uploadId) {
        await deleteFile(uploadId);
      }
    } catch (err) {
      console.error("Error deleting file:", err);
      showToast(
        t("tripPublication.addTrip.upload.deleteError", "Le fichier n'a pas pu être supprimé."),
        "error"
      );
    } finally {
      setIsDeleting(false);
    }
    onChange("");
  };

  return (
    <div className={styles.wrapper}>
      <Text as="label">{label}</Text>
      <div className={styles.uploadRow}>
        <UploadFile
          t={t}
          fileList={fileList}
          onUpload={guardOnUpload(handleFilePick)}
          multiple={false}
          className={styles.uploadFile}
          accept={getAcceptAttribute(STANDARD_UPLOAD_EXTENSIONS)}
        />
        <UploadFileError message={fileError} />
        {!autoUpload ? (
          <Button
            label={isLocalUploading ? t("tripPublication.addTrip.upload.uploading") : t("tripPublication.addTrip.upload.saveImage")}
            onClick={() => handleUpload()}
            disabled={!fileList?.length || isLocalUploading || isDeleting}
            className={styles.uploadBtn}
          />
        ) : null}
        {isLocalUploading ? (
          <div className={styles.loadingRow}>
            <FontAwesomeIcon icon={faSpinner} className={styles.spinner} />
            <Text as="span" size="sm">{t("tripPublication.addTrip.upload.uploading")}</Text>
          </div>
        ) : null}
      </div>

      {value && !fileList?.length ? (
        <div className={styles.previewRow}>
          <UploadedFile t={t}
            fileName="PICTURE"
            index={0}
            isEditMode={true}
            removeFile={handleRemoveUploaded}
          />
          {isDeleting ? (
            <div className={styles.loadingRow}>
              <FontAwesomeIcon icon={faSpinner} className={styles.spinner} />
              <Text as="span" size="sm">{t("tripPublication.addTrip.upload.deleting")}</Text>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default UploadImageField;
